import { selMessage } from "@selector/shared";
import { clsx } from "clsx";
import { Component, type FC, type ReactElement, useRef } from "react";
import { connect } from "react-redux";
import { LoadingSpinner, Tag, TagIntent, Tooltip, useTooltip } from "widgets";
import { useResizeObserver } from "widgets/hooks";

import { pushRoute } from "@/app-utils/route.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type {
  AnalystSession,
  CoordinateList,
  Group,
  Parent,
  RegionFlag,
  SavedItem,
  SavedItemsItemsArgs,
  SavedItemsListing,
  View,
} from "@/graphql.ts";
import { getSavedItemsLists, type IExtendedSavedItemData } from "@/redux/modules/dashboard.ts";
import { downloadJnlpFile, type IJnlpDownloadInputData } from "@/redux/modules/files.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { EXT_APP_MODULES, ITEMS_TYPE } from "@/settings/settings.ts";

import {
  type IDashboardCardSavedItemsList,
  loadDashboardCardSavedItemsData,
  loadDashboardCardSavedItemsListing,
  matchSavedItemsType,
} from "../../redux/modules/dashboard.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import GenericCard, { type IGenericCardProps } from "../core/GenericCard.js";
import {
  AnalystSessionTileMapper,
  CoordListTileMapper,
  GroupTileMapper,
  ParentTileMapper,
  RegionFlagTileMapper,
  ViewTileMapper,
} from "./TilesMappers.js";

export interface ISavedItemsCardConfig {
  limit: number;
}

/*--- Redux --- */
interface ISavedItemsDashboardCardAppStateProps {
  savedItems: IDashboardCardSavedItemsList;
}

function mapStateToProps(state: IAppState, ownProps: IGenericCardProps): ISavedItemsDashboardCardAppStateProps {
  return {
    savedItems: state.dashboardState.savedItemsCards[ownProps.id],
  };
}

interface ISavedItemsDashboardCardDispatchProps {
  loadSavedItemsListing: () => Promise<SavedItemsListing[]>;
  loadSavedItems: (savedItemsListing: SavedItemsItemsArgs, filterTerm: string) => Promise<SavedItem[]>;
  downloadJnlpFile: (jnlpDownloadPayload: IJnlpDownloadInputData) => void;
  viewSavedItemsInContentBrowser: (type: string, subtype: string) => void;
}

function mapDispatchToProps(dispatch, ownProps: IGenericCardProps): ISavedItemsDashboardCardDispatchProps {
  return {
    loadSavedItemsListing: () =>
      dispatch(
        loadDashboardCardSavedItemsListing(
          ownProps.id,
          ownProps.config && (ownProps.config.specificConfig as ISavedItemsCardConfig),
        ),
      ),
    loadSavedItems: (savedItemsListing: SavedItemsItemsArgs, filterTerm: string) =>
      dispatch(
        loadDashboardCardSavedItemsData(
          ownProps.id,
          ownProps.config && (ownProps.config.specificConfig as ISavedItemsCardConfig),
          savedItemsListing,
          filterTerm,
        ),
      ),
    downloadJnlpFile: (jnlpDownloadPayload: IJnlpDownloadInputData) => dispatch(downloadJnlpFile(jnlpDownloadPayload)),
    viewSavedItemsInContentBrowser: (type: string, subtype: string) =>
      pushRoute(`/content/savedItems/${type}/${subtype}`),
  };
}

/* --- Component --- */
interface ISavedItemsDashboardCardProps
  extends ISavedItemsDashboardCardAppStateProps,
    ISavedItemsDashboardCardDispatchProps,
    IGenericCardProps {}

interface ISavedItemsDashboardCardState {
  activeSavedItemsListing: SavedItemsItemsArgs | null;
  filterTerm: string | null; // for the filter term to be in the card title trailer
  scrollMore: boolean;
}

class SavedItemsDashboardCard extends Component<ISavedItemsDashboardCardProps, ISavedItemsDashboardCardState> {
  state: ISavedItemsDashboardCardState = {
    activeSavedItemsListing: null,
    filterTerm: null,
    scrollMore: false,
  };

  componentDidMount() {
    // loads category and first items
    void this.props.loadSavedItemsListing().then((listings: SavedItemsListing[]) => {
      // first page opens initially
      const initialListing: SavedItemsListing = listings.filter((listing) => listing.count > 0)?.[0];
      if (initialListing) {
        this.switchListing(initialListing);
      }
    });
  }

  scrollMoreCallback(scrollMore: boolean) {
    if (this.state.scrollMore !== scrollMore) {
      this.setState({ scrollMore });
    }
  }

  switchListing = (savedItemsListing: SavedItemsListing) => {
    this.setState({
      activeSavedItemsListing: savedItemsListing,
      filterTerm: "",
    });
    void this.props.loadSavedItems(savedItemsListing, "");
  };

  refreshData = () => {
    return this.props
      .loadSavedItemsListing()
      .then(() => this.props.loadSavedItems(this.state.activeSavedItemsListing, this.state.filterTerm));
  };

  goToFullVersion = () => {
    this.props.viewSavedItemsInContentBrowser(
      this.state.activeSavedItemsListing.type,
      this.state.activeSavedItemsListing.subType,
    );
  };

  setFilterSearch = (filterTerm: string, _filterTerms: string[]) => {
    this.setState({ filterTerm }); // not using filterTerms currently
    return this.props.loadSavedItems(this.state.activeSavedItemsListing, filterTerm);
  };

  render() {
    const activeSavedItemsData: IExtendedSavedItemData | undefined | null =
      this.state.activeSavedItemsListing &&
      this.props.savedItems?.listing?.find(
        (i) =>
          i.meta?.type === this.state.activeSavedItemsListing!.type &&
          i.meta?.subType === this.state.activeSavedItemsListing!.subType,
      );

    const titleTrailer: string = this.state.filterTerm ? `filtered by "${this.state.filterTerm}"` : "";
    const cardIsLoading = this.props.savedItems?.isLoading || activeSavedItemsData?.isLoadingItems;

    const filterElement: ReactElement = (
      <div className={"card-filter"}>
        {/* filter input field without label */}
        <AsyncSearchWithExprParser
          onSearchExprChange={this.setFilterSearch}
          controlledValue={this.state.filterTerm}
          placeholder={"Filter"}
        />
      </div>
    );

    const showButtonMoreItems =
      activeSavedItemsData?.items &&
      activeSavedItemsData?.meta &&
      !activeSavedItemsData.isLoadingItems &&
      ((activeSavedItemsData.items.length < activeSavedItemsData.meta.count &&
        activeSavedItemsData.items.length >= (this.props.config.specificConfig as ISavedItemsCardConfig).limit) ||
        this.state.scrollMore);

    // filter out the empty lists
    const savedItemsListing: IExtendedSavedItemData[] = getSavedItemsLists(this.props.savedItems?.listing);

    return (
      <GenericCard
        {...this.props}
        cardPanelClassName={"saved-items-card"}
        refreshData={this.refreshData}
        goToAction={this.goToFullVersion}
        titleTrailer={titleTrailer}
        titleElement={filterElement}
        scrollMoreCallback={(s) => {
          this.scrollMoreCallback(s);
        }}
        tipTourId={"saved-items-title"}
        footerContent={
          showButtonMoreItems ? (
            <span>
              There are <a onClick={this.goToFullVersion}>more {pluralizeWord(activeSavedItemsData?.meta?.name)}</a> in
              Saved Items.
            </span>
          ) : undefined
        }
      >
        {this.props.savedItems && !this.props.savedItems.isLoading && savedItemsListing.length === 0 && (
          <ContentPlaceholder>{selMessage({ id: "view.placeholder" })}</ContentPlaceholder>
        )}

        <div className={"saved-items-body"}>
          <div className={clsx("saved-items-menu", { "loading-overlay": !!this.props.savedItems?.isLoading })}>
            {savedItemsListing.map(
              (item) =>
                item.meta && (
                  <CategoryLink
                    item={item}
                    activeSavedItemsListing={this.state.activeSavedItemsListing}
                    switchListing={this.switchListing.bind(this)}
                    key={`${item.meta.type}-${item.meta?.subType}`}
                  />
                ),
            )}
          </div>

          <div
            className={clsx("saved-items-panel", {
              "loading-overlay": cardIsLoading,
            })}
          >
            {cardIsLoading && <LoadingSpinner style={{ marginTop: 100, zIndex: 5 }} />}
            {!cardIsLoading && activeSavedItemsData?.meta && activeSavedItemsData?.items?.length === 0 && (
              <ContentPlaceholder
                text={`There are no ${
                  this.state.filterTerm == undefined ? "" : '"' + this.state.filterTerm + '"'
                } ${pluralizeWord(activeSavedItemsData.meta.name)}`}
              />
            )}
            {activeSavedItemsData?.meta && (activeSavedItemsData?.items?.length ?? 0) > 0 && (
              <div
                className="tiles_grid animated"
                key={`${activeSavedItemsData.meta.type}-${activeSavedItemsData.meta.subType}`}
              >
                {activeSavedItemsData.items?.map((item: SavedItem) => this.getTileMapper(activeSavedItemsData, item))}
              </div>
            )}
          </div>
        </div>
      </GenericCard>
    );
  }

  getTileMapper = (activeSavedItemsData: IExtendedSavedItemData, item: SavedItem) => {
    switch (activeSavedItemsData?.meta?.type) {
      case ITEMS_TYPE.VIEW:
        return <ViewTileMapper key={item.id} data={item as View} />;
      case ITEMS_TYPE.GROUP:
        return <GroupTileMapper key={item.id} data={item as Group} />;
      case ITEMS_TYPE.SAMPLE_PARENT:
        return <ParentTileMapper key={item.id} data={item as Parent} />;
      case ITEMS_TYPE.REGION_FLAG:
        return <RegionFlagTileMapper key={item.id} data={item as RegionFlag} />;
      case ITEMS_TYPE.COORD_LIST:
        return <CoordListTileMapper key={item.id} data={item as CoordinateList} />;
      case ITEMS_TYPE.ANALYST_SESSION:
        return (
          <AnalystSessionTileMapper key={item.id} onClick={this.triggerJnlpDownload} data={item as AnalystSession} />
        );
      default:
        throw Error("Could not find a proper Mapper for Card Tiles of saved items");
    }
  };

  triggerJnlpDownload = (item: AnalystSession) => {
    const jnlpDownloadPayload: IJnlpDownloadInputData = {
      moduleId: EXT_APP_MODULES.ANALYST.id,
      sessionId: item.id,
    };
    this.props.downloadJnlpFile(jnlpDownloadPayload);
  };
}

interface CategoryLinkProps {
  item: IExtendedSavedItemData;
  activeSavedItemsListing: SavedItemsItemsArgs | null;
  switchListing: (items?: SavedItemsListing) => void;
}

const CategoryLink: FC<CategoryLinkProps> = ({ item, activeSavedItemsListing, switchListing }) => {
  const ref = useRef(null);
  const rect = useResizeObserver(ref);

  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const { state, tooltipProps, triggerProps, arrowProps } = useTooltip(triggerRef, tooltipRef, {
    isDisabled: !rect?.widthOverflow,
    placement: "right",
  });

  if (!item.meta) return null;

  return (
    <>
      <div
        {...triggerProps}
        onClick={() => switchListing(item.meta)}
        className={clsx("saved-items-menu-item", {
          "active-menu-item": matchSavedItemsType(item.meta, activeSavedItemsListing),
        })}
        ref={triggerRef}
      >
        <span className="saved-items-menu-item--title" ref={ref}>
          {pluralizeWord(item.meta.name ?? "")}
        </span>
        <Tag label={item.meta.count} intentColor={TagIntent.Outlined} />
      </div>
      {state.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          ref={tooltipRef}
          arrowProps={arrowProps}
          content={pluralizeWord(item.meta.name ?? "")}
        />
      )}
    </>
  );
};

export default connect<ISavedItemsDashboardCardAppStateProps, ISavedItemsDashboardCardDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(SavedItemsDashboardCard);
